import React, { useEffect, useRef, useState } from 'react';

import moment from 'moment';
import { connect, shallowEqual } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadContactFiles } from '../contact-file/actions/load-contact-files';
import { contactFilesForContactIdSelector } from '../contact-file/contact-file-selectors';
import { cancelScheduledText } from '../contacts/actions/cancel-scheduled-text';
import { loadContactMessages } from '../contacts/actions/load-contact-messages';
import { updateScheduledMessage } from '../contacts/actions/update-scheduled-message';
import { loadContactMessageTranslations } from '../message-translation/actions/load-contact-message-translations';
import {
  messagesForEmployeeIdSelector,
  scheduledMessagesForEmployeeIdSelector,
} from '../reducers/contact-selectors';
import { employeeSelector } from '../reducers/employee-selectors';
import { orgSelector } from '../reducers/org-selectors';
import { loadShortenedUrlVisits } from '../shortened-urls/actions/load-shortened-url-visits';
import {
  visitDetailsForContactIdSelector,
  visitDetailsStateForContactIdSelector,
} from '../shortened-urls/shortened-url-selectors';

import { FeatureFlagged, FeatureGatedVariant } from '../feature-gates/feature-flagged';
import { FeatureGates } from '../feature-gates/feature-gates';
import { MessageList } from '../messages/components/message-list';
import { LegacyMessageList } from '../messages/legacy-components/legacy-message-list';
import { UiButton } from '../ui/ui-button';

import { formatDateTime, formatRelativeDate, truncateConversation } from '../util/formatters';
import { LanguageCode } from '../util/languages';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { MESSAGE_TYPE } from '../util/message-types';
import { confirm } from '../widgets/confirmation-dialog';
import { LegacyPendingScheduledMessageSection } from '../widgets/legacy-pending-message-section';
import { LoadingMessage } from '../widgets/loading-message';
import { collectMessageToLanguage } from '../widgets/message-composer/wrappers/multiple-message-composer-dialog';
import { PendingScheduledMessageSection } from '../widgets/pending-message-section';

const sortScheduledMessage = (a: ScheduledMessage, b: ScheduledMessage) =>
  moment.utc(a.sendDateTime).valueOf() - moment.utc(b.sendDateTime).valueOf();

const mapStateToProps = (state: any, ownProps: InputProps) => {
  const employee = employeeSelector(state, ownProps.employeeId);
  let messages, scheduledMessages, org, urlVisitDetails, urlVisitDetailsState, contactFiles;
  if (employee) {
    const { contactId, employeeId, orgId } = employee;
    messages = messagesForEmployeeIdSelector(state, contactId, employeeId);
    scheduledMessages = scheduledMessagesForEmployeeIdSelector(state, contactId, employeeId);
    org = orgSelector(state, orgId);
    urlVisitDetails = visitDetailsForContactIdSelector(state, contactId);
    // this state is unused in this file, but is needed to trigger reload when state is set dirty
    urlVisitDetailsState = visitDetailsStateForContactIdSelector(state, contactId);
    contactFiles = contactFilesForContactIdSelector(state, contactId);
  }

  return {
    employee,
    messages,
    scheduledMessages,
    org,
    urlVisitDetails,
    urlVisitDetailsState,
    contactFiles,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    loadContactMessages: (contactId: number) => dispatch(loadContactMessages(contactId)),
    loadContactMessageTranslations: (contactId: number) =>
      dispatch(loadContactMessageTranslations(contactId)),
    loadContactFiles: (contactId: number) => dispatch(loadContactFiles(contactId)),
    loadShortenedUrlVisits: (contactId: number) => dispatch(loadShortenedUrlVisits(contactId)),
    updateScheduledMessage: (
      contactId: number,
      scheduledMessageId: number,
      update: Partial<ScheduledMessage>,
    ) => dispatch(updateScheduledMessage(contactId, scheduledMessageId, update)),
    cancelScheduledMessage: (contactId: number, scheduledMessageId: number) =>
      dispatch(cancelScheduledText(contactId, scheduledMessageId)),
  },
});

type InputProps = {
  employeeId: number;
  onMessagesLoaded?: () => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeTextMessageList = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    onMessagesLoaded,
    employee,
    messages,
    scheduledMessages,
    org,
    urlVisitDetails,
    contactFiles,
    actions,
  }: Props) => {
    const [showAllMessages, setShowAllMessages] = useState<boolean>(false);

    useEffect(() => {
      if (employee) {
        actions.loadContactMessages(employee.contactId);
        actions.loadContactMessageTranslations(employee.contactId);
        actions.loadContactFiles(employee.contactId);
        actions.loadShortenedUrlVisits(employee.contactId);
      }
    });

    const previousMessagesRef = useRef<Message[] | undefined>(undefined);
    useEffect(() => {
      // Because this component handles scrolling the the message composer into
      // view in the employee-messages component, we need to use shallowEqual
      // to prevent accidental renders that will scroll the page to the bottom
      // this is especially important when streaming message translations.
      // Ideally the employee-messages component should scroll the composer
      // into view when the messages have finished loading in redux.
      if (messages && onMessagesLoaded && !shallowEqual(previousMessagesRef.current, messages)) {
        onMessagesLoaded();
      }
      previousMessagesRef.current = messages;
    }, [contactFiles, messages, onMessagesLoaded]);

    if (!messages || !employee || !scheduledMessages || !org) {
      return <LoadingMessage />;
    }

    const handleEditScheduledMessage = (scheduledMessage: ScheduledMessage) => async () => {
      const languageToMessage: Record<LanguageCode, string> = {
        en: scheduledMessage.message,
      };
      const languagesToSupport = ['en'];

      const returnVal = await collectMessageToLanguage(
        'Edit Scheduled Message',
        `Edit this message. It will be sent ${formatRelativeDate(
          scheduledMessage.sendDateTime,
        )}, on ${formatDateTime(scheduledMessage.sendDateTime)}.`,
        {
          enableText: scheduledMessage.messageType === MESSAGE_TYPE.TEXT,
          enableEmail: scheduledMessage.messageType === MESSAGE_TYPE.EMAIL,
          languageToSubject: scheduledMessage.subject
            ? { en: scheduledMessage.subject }
            : undefined,
          languageToMessage,
          languagesToSupport,
          buttonClass: 'u-float-right',
          buttonText: 'Save Message',
        },
      );

      if (returnVal) {
        actions.updateScheduledMessage(
          scheduledMessage.contactId,
          scheduledMessage.scheduledMessageId,
          {
            message: returnVal.languageToMessage.en,
            subject: returnVal.languageToSubject.en,
          },
        );
      }
    };

    const handleDeleteScheduledMessage = (scheduledMessage: ScheduledMessage) => async () => {
      if (await confirm('Are you sure you want to cancel this message?')) {
        actions.cancelScheduledMessage(
          scheduledMessage.contactId,
          scheduledMessage.scheduledMessageId,
        );
      }
    };

    const handleShowAllMessagesClick = () => setShowAllMessages(true);

    const employeeName = {
      firstName: employee.Contact.Person.firstName,
      lastName: employee.Contact.Person.lastName,
    };

    const messagesToShow: Message[] = showAllMessages
      ? messages || []
      : truncateConversation(messages || [], (m: Message) => m.activity.datetime);

    // Announcements can be scheduled for the future. The ones that aren't might have up to a five minute delay. We want
    // to distinguish these two cases as the difference between "scheduled" and "pending"
    const pendingMessages = scheduledMessages
      .filter(
        (sm) => sm.willSendInFuture && moment.utc(sm.sendDateTime).isSameOrBefore(moment.utc()),
      )
      .sort(sortScheduledMessage);

    const futureMessages = scheduledMessages
      .filter((sm) => sm.willSendInFuture && moment.utc(sm.sendDateTime).isAfter(moment.utc()))
      .sort(sortScheduledMessage);

    return (
      <>
        {messagesToShow.length !== messages?.length && !showAllMessages && (
          <UiButton
            variant="tertiary"
            size="sm"
            className="u-margin-top u-margin-bottom u-width-100"
            onClick={handleShowAllMessagesClick}
          >
            Show Previous Messages
          </UiButton>
        )}
        <FeatureFlagged
          variant={FeatureGatedVariant.HIDE_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <LegacyMessageList
            messages={messagesToShow}
            name={employeeName}
            orgName={org.name}
            orgId={org.org_id}
            urlVisitDetails={urlVisitDetails}
          />
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <MessageList
            messages={messagesToShow}
            name={employeeName}
            orgName={org.name}
            orgId={org.org_id}
            urlVisitDetails={urlVisitDetails}
          />
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.HIDE_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <LegacyPendingScheduledMessageSection
            defaultExpanded
            title="Messages Sending Now"
            icon={<MaterialDesignIcon iconName={ICON_NAMES.SEND} color="secondary" />}
            messages={pendingMessages}
          />
          <LegacyPendingScheduledMessageSection
            title="Scheduled Messages"
            icon={<MaterialDesignIcon iconName={ICON_NAMES.EVENT} color="secondary" />}
            messages={futureMessages}
            onEditMessageGenerator={handleEditScheduledMessage}
            onCancelMessageGenerator={handleDeleteScheduledMessage}
          />
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <PendingScheduledMessageSection
            defaultExpanded
            title="Messages Sending Now"
            icon={<MaterialDesignIcon iconName={ICON_NAMES.SEND} color="secondary" />}
            messages={pendingMessages}
            recipientName={`${employeeName.firstName} ${employeeName.lastName}`}
            orgId={org.org_id}
          />
          <PendingScheduledMessageSection
            title="Scheduled Messages"
            icon={<MaterialDesignIcon iconName={ICON_NAMES.EVENT} color="secondary" />}
            messages={futureMessages}
            onEditMessageGenerator={handleEditScheduledMessage}
            onCancelMessageGenerator={handleDeleteScheduledMessage}
            recipientName={`${employeeName.firstName} ${employeeName.lastName}`}
            orgId={org.org_id}
          />
        </FeatureFlagged>
      </>
    );
  },
);
